/**
 * TwoSidedCTA — one large split interaction after the video hero.
 *
 * The scroll-video hero stays unchanged. This section continues the soft,
 * dirty-white world from the animation and lets the visitor move between the
 * two marketplace sides by moving across the panel.
 */

import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MoveHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { cn } from "@/lib/cn";

type PanelCopy = {
  label: string;
  title: string;
  body: string;
  imageAlt: string;
};

type EarlyAccessRole = "shipper" | "carrier";

const MIN_SPLIT = 5;
const MAX_SPLIT = 95;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 100, damping: 20 } },
};

function SplitVisual() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const carrierLayerRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const splitRef = useRef(50);
  const rafRef = useRef<number | null>(null);
  const [splitForA11y, setSplitForA11y] = useState(50);
  const [isEasing, setIsEasing] = useState(false);

  const sender = t("twoSided.visual.sender", { returnObjects: true }) as PanelCopy;
  const carrier = t("twoSided.visual.carrier", { returnObjects: true }) as PanelCopy;

  // Apply the visual position directly to the DOM. Bypassing React state per
  // pointer-move keeps the divider locked to the cursor at full frame rate
  // instead of being re-rendered + transitioned each event.
  const applySplit = (next: number) => {
    const clamped = Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, next));
    splitRef.current = clamped;
    if (carrierLayerRef.current) {
      carrierLayerRef.current.style.clipPath = `inset(0 0 0 ${clamped}%)`;
    }
    if (dividerRef.current) {
      dividerRef.current.style.left = `${clamped}%`;
    }
    return clamped;
  };

  const setSplitFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    if (isEasing) setIsEasing(false);
    const rect = event.currentTarget.getBoundingClientRect();
    const clientX = event.clientX;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      applySplit(((clientX - rect.left) / rect.width) * 100);
    });
  };

  const handlePointerLeave = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setIsEasing(true);
    applySplit(50);
    setSplitForA11y(50);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setIsEasing(true);
      const next = applySplit(splitRef.current - 7);
      setSplitForA11y(Math.round(next));
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      setIsEasing(true);
      const next = applySplit(splitRef.current + 7);
      setSplitForA11y(Math.round(next));
    }
  };

  const easeStyles = "transition-[clip-path,left] duration-[280ms] ease-[cubic-bezier(0.22,1,0.36,1)]";

  return (
    <div
      ref={containerRef}
      className="group relative min-h-[clamp(22rem,58dvh,38rem)] cursor-ew-resize select-none overflow-hidden rounded-[2rem] border border-[#d2cabb] bg-[#e8e1d5] shadow-[0_28px_80px_-60px_rgba(15,23,42,0.75)] outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-4 focus-visible:ring-offset-[#e5ded2]"
      role="slider"
      tabIndex={0}
      aria-label={t("twoSided.visual.sliderLabel")}
      aria-valuemin={MIN_SPLIT}
      aria-valuemax={MAX_SPLIT}
      aria-valuenow={splitForA11y}
      onPointerMove={setSplitFromPointer}
      onPointerLeave={handlePointerLeave}
      onKeyDown={handleKeyDown}
    >
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(37,99,235,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(37,99,235,0.05)_1px,transparent_1px)] bg-[size:36px_36px]" />

      <div className="absolute inset-0 z-0">
        <PanelLayer
          copy={sender}
          imageSrc="/landing-assets/sender-ordering-transport.png"
          mobileImageSrc="/landing-assets/mobile/sender-ordering-transport.webp"
          side="sender"
        />
      </div>
      <div
        ref={carrierLayerRef}
        className={cn("absolute inset-0 z-10 [will-change:clip-path]", isEasing && easeStyles)}
        style={{ clipPath: "inset(0 0 0 50%)" }}
      >
        <PanelLayer
          copy={carrier}
          imageSrc="/landing-assets/carrier-loading-cargo.png"
          mobileImageSrc="/landing-assets/mobile/carrier-loading-cargo.webp"
          side="carrier"
        />
      </div>

      <div
        ref={dividerRef}
        className={cn("absolute inset-y-4 z-20 w-px bg-brand-600/90 [will-change:left]", isEasing && easeStyles)}
        style={{ left: "50%" }}
        aria-hidden
      >
        <span className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-brand-200 bg-[#f6f2ea] text-brand-700 shadow-[0_18px_50px_-28px_rgba(15,23,42,0.7)]">
          <MoveHorizontal className="h-5 w-5" strokeWidth={1.75} />
        </span>
      </div>

      <div className="pointer-events-none absolute bottom-5 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full border border-slate-300/80 bg-[#f7f3eb]/85 px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.8)] backdrop-blur md:bottom-6">
        <MoveHorizontal className="h-3.5 w-3.5 text-brand-700" strokeWidth={1.75} aria-hidden />
        <span>{t("twoSided.visual.hint")}</span>
      </div>
    </div>
  );
}

function PanelLayer({
  copy,
  imageSrc,
  mobileImageSrc,
  side,
}: {
  copy: PanelCopy;
  imageSrc: string;
  mobileImageSrc: string;
  side: "sender" | "carrier";
}) {
  const isCarrier = side === "carrier";

  return (
    <div
      className={cn(
        "absolute inset-0 isolate overflow-hidden",
        isCarrier ? "bg-[#e7dfd3]" : "bg-[#ebe4d8]",
      )}
    >
      <picture>
        <source media="(max-width: 768px)" srcSet={mobileImageSrc} type="image/webp" />
        <img
          src={imageSrc}
          alt={copy.imageAlt}
          width={1792}
          height={1024}
          loading="lazy"
          decoding="async"
          className={cn(
            "absolute inset-0 h-full w-full object-cover",
            isCarrier ? "object-[68%_75%]" : "object-[15%_30%]",
          )}
        />
      </picture>
      <div
        className={cn(
          "absolute inset-0",
          isCarrier
            ? "bg-[linear-gradient(90deg,rgba(232,225,213,0),rgba(232,225,213,0.14)_42%,rgba(232,225,213,0.78)_82%)]"
            : "bg-[linear-gradient(90deg,rgba(232,225,213,0.8)_0%,rgba(232,225,213,0.2)_42%,rgba(232,225,213,0)_78%)]",
        )}
      />
      <div
        className={cn(
          "absolute top-5 max-w-[18rem] rounded-3xl border border-[#d8d0c2]/80 bg-[#f8f4ec]/75 p-5 shadow-[0_24px_80px_-58px_rgba(15,23,42,0.8)] backdrop-blur-md sm:top-7 md:top-8 md:max-w-[22rem] md:p-6",
          isCarrier ? "right-5 text-right md:right-8" : "left-5 md:left-8",
        )}
      >
        <span className="inline-flex rounded-full border border-slate-300/80 bg-[#f7f3eb]/85 px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500 backdrop-blur">
          {copy.label}
        </span>
        <h3 className="mt-4 text-3xl font-bold leading-[1.02] tracking-tight text-slate-950 md:text-4xl">
          {copy.title}
        </h3>
        <p
          className={cn("mt-3 text-sm leading-relaxed text-slate-600 md:text-base", isCarrier ? "ml-auto" : "")}
        >
          {copy.body}
        </p>
      </div>
    </div>
  );
}

export function TwoSidedCTA() {
  const { t } = useTranslation();

  const openEarlyAccess = (role: EarlyAccessRole) => {
    window.dispatchEvent(new CustomEvent("vegr:open-early-access", { detail: { role } }));
  };

  return (
    <section
      className="relative flex min-h-[100dvh] items-center overflow-hidden bg-[#e5ded2] py-[clamp(2.5rem,7dvh,6rem)] text-slate-950"
      id="for-deg"
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,#d9d1c4,#e5ded2_28%,#e9e2d6)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(30,41,59,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(30,41,59,0.045)_1px,transparent_1px)] bg-[size:56px_56px]" />

      <Container className="relative max-w-7xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUp}
          className="grid grid-cols-1 gap-8 lg:grid-cols-[0.72fr_1fr] lg:items-end"
        >
          <div className="pt-6 md:pt-10">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-700">
              {t("twoSided.label")}
            </span>
            <h2 className="mt-5 max-w-[14ch] text-3xl font-bold leading-[1.02] tracking-tight text-slate-950 md:text-5xl">
              {t("twoSided.title")}
            </h2>
          </div>
          <div className="max-w-xl lg:justify-self-end">
            <p className="text-lg leading-relaxed text-slate-600">
              {t("twoSided.subtitle")}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => openEarlyAccess("shipper")}
              >
                {t("twoSided.visual.sender.cta")}
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden />
              </Button>
              <Button
                size="lg"
                variant="secondary"
                className="w-full border border-slate-300 bg-[#f7f3eb] text-slate-950 hover:bg-white sm:w-auto"
                onClick={() => openEarlyAccess("carrier")}
              >
                {t("twoSided.visual.carrier.cta")}
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden />
              </Button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mt-10 lg:-mx-6 lg:mt-12 xl:-mx-12 2xl:-mx-20"
        >
          <SplitVisual />
        </motion.div>
      </Container>
    </section>
  );
}
